import React, { useMemo, useRef, useEffect, useState } from "react";
import PropTypes from "prop-types";

const ShippingProgress = ({ takeback, shippingData }) => {
  const [lineHeight, setLineHeight] = useState(0);
  const dotRefs = useRef([]);

  // Extract shipping tracking number
  const trackingNumber =
    shippingData?.[0]?.tracking_number ||
    takeback?.data?.u_ups_tracking_number ||
    takeback?.shipping_tracking_id;

  // Determine if shipping label exists - check all possible fields
  const hasShippingLabel = !!(
    takeback?.data?.u_ups_shipping_label_url ||
    takeback?.data?.u_ups_shipping_label_encoded ||
    takeback?.data?.u_lego_dhl_label_b64_encoded ||
    takeback?.data?.legoValue || // If payment was issued, there must have been shipping
    trackingNumber
  );

  // Get dates for different stages
  const labelCreatedDate =
    takeback?.data?.u_ups_label_created_at ||
    takeback?.data?.inStoreVerifiedAt || // Use verification date as label creation for in-store
    (hasShippingLabel ? takeback?.created_at : null);
  const receivedDate = takeback?.data?.u_lego_received_at;

  // Define shipping progress steps
  const steps = useMemo(
    () => [
      {
        label: "Label created",
        date: labelCreatedDate,
        active: !!labelCreatedDate,
      },
      {
        label: "Package collected",
        date: null, // This will always show as pending
        active: false,
      },
      {
        label: "Arrived at facility",
        date: receivedDate,
        active: !!receivedDate,
      },
    ],
    [labelCreatedDate, receivedDate]
  );

  // Calculate which step is active (last completed step)
  const activeIndex = useMemo(() => {
    return steps.reduce((latestIndex, step, index) => {
      if (step.active && step.date) return index;
      return latestIndex;
    }, -1);
  }, [steps]);

  // Update line height for mobile timeline
  useEffect(() => {
    if (dotRefs.current[activeIndex]) {
      const activeDotTop = dotRefs.current[activeIndex].offsetTop;
      setLineHeight(activeDotTop);
    }
  }, [activeIndex, steps.length]);

  // Don't show shipping progress if no label exists
  if (!hasShippingLabel) return null;

  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
  };

  return (
    <div className="bg-white border-t border-t-gray-300 p-6 mt-6">
      <div className="flex items-center gap-3 mb-6">
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          className="text-gray-600"
        >
          <path
            d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"
            fill="currentColor"
          />
        </svg>
        <h3 className="text-xl font-semibold">Shipping</h3>
      </div>

      {/* Mobile timeline */}
      <div className="lg:hidden relative w-full flex flex-col gap-8 my-4">
        {/* Background gray line */}
        <div
          className={`absolute left-2 top-2 ${
            activeIndex === steps.length - 1 ? "bottom-6" : "bottom-2"
          } w-1 bg-gray-300`}
        ></div>

        {/* Active green progress line */}
        <div
          className="absolute left-2 w-1 bg-green"
          style={{
            top: "0.5rem",
            height: `${lineHeight}px`,
            transition: "height 0.3s ease",
          }}
        ></div>

        {/* Steps */}
        {steps.map((step, index) => (
          <div
            className="flex"
            key={index}
            ref={(el) => (dotRefs.current[index] = el)}
          >
            {/* Progress dot */}
            <div
              className={`mt-1 w-5 h-5 rounded-full border-2 border-white z-10 ${
                index <= activeIndex ? "bg-green" : "bg-gray-300"
              }`}
            ></div>
            {/* Label and date */}
            <div className={`ml-4 mt-1 text-sm`}>
              <div className={`${index === activeIndex ? "font-bold" : ""}`}>
                {step.label}
              </div>
              {step.date && (
                <div className="text-tertiary">{formatDate(step.date)}</div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Desktop timeline */}
      <div className="hidden lg:flex relative w-full items-start justify-between pb-6">
        {/* Background gray line */}
        <div className="absolute left-0 right-0 top-2 h-1 bg-gray-300"></div>

        {/* Active green progress line */}
        <div
          className="absolute left-0 h-1 top-2 bg-green"
          style={{
            width: `${(activeIndex / (steps.length - 1)) * 100}%`,
            transition: "width 0.3s ease",
          }}
        ></div>

        {/* Steps */}
        {steps.map((step, index) => (
          <div key={index} className="relative flex flex-col items-center h-14">
            {/* Progress dot */}
            <div
              className={`w-5 h-5 rounded-full border-2 border-white z-10 ${
                index <= activeIndex ? "bg-green" : "bg-gray-300"
              }`}
            ></div>
            {/* Label and date container */}
            <div
              className={`absolute top-6 text-sm flex flex-col  ${
                index === 0
                  ? "text-left left-0 items-start"
                  : index === steps.length - 1
                    ? "text-right right-0 items-end"
                    : "text-center items-center"
              }`}
            >
              <div
                className={`min-w-[160px] ${
                  index === activeIndex ? "font-bold" : ""
                }`}
              >
                {step.label}
              </div>
              {step.date && (
                <div className="text-tertiary">{formatDate(step.date)}</div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* UPS Tracking Link */}
      {trackingNumber && (
        <div className="pt-2 pb-2 flex items-center">
          <a
            href={`https://www.ups.com/track?loc=en_US&tracknum=${trackingNumber}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 group flex items-center gap-1"
          >
            <span className="group-hover:underline">View tracking on UPS</span>
            <span className="material-symbols-outlined scale-75">
              open_in_new
            </span>
          </a>
        </div>
      )}
    </div>
  );
};

ShippingProgress.propTypes = {
  takeback: PropTypes.object.isRequired,
  shippingData: PropTypes.array,
};

export default ShippingProgress;
